import { generatePath, matchPath } from 'react-router-dom';
import { paths, type TaskRoutePaths } from '@sidecar/config/paths';

export const TASK_ROUTE_KEY = {
  root: 'root',
  new: 'new',
  details: 'details',
  edit: 'edit',
  subtasks: 'subtasks',
  subtasksNew: 'subtasksNew',
  subtasksEdit: 'subtasksEdit',
  comments: 'comments',
  commentsNew: 'commentsNew',
  activityLog: 'activityLog',
} as const satisfies Record<string, keyof TaskRoutePaths>;

export type TaskRouteKey = (typeof TASK_ROUTE_KEY)[keyof typeof TASK_ROUTE_KEY];

export type TaskRouteParams = {
  organizationId?: string;
  projectId?: string;
  vendorId?: string;
  taskId?: string;
  subtaskId?: string;
};

export interface TaskDetailsTabRouteState {
  isSubtasksActive: boolean;
  isAddingSubtask: boolean;
  editingSubtaskId: string | null;
  isCommentsActive: boolean;
  isAddingComment: boolean;
  isActivityLogActive: boolean;
}

export const EMPTY_TASK_DETAILS_TAB_ROUTE_STATE: TaskDetailsTabRouteState = {
  isSubtasksActive: false,
  isAddingSubtask: false,
  editingSubtaskId: null,
  isCommentsActive: false,
  isAddingComment: false,
  isActivityLogActive: false,
};

const TASK_CONTEXTS = [
  paths.dashboard.tasks,
  paths.organizations.details.work.tasks,
  paths.projects.details.tasks,
  paths.vendors.details.tasks,
  paths.work.tasks,
] as const satisfies ReadonlyArray<TaskRoutePaths>;

const TASK_PATH_FALLBACKS = [
  {
    path: `${paths.organizations.details.root}/*`,
    taskPaths: paths.organizations.details.work.tasks,
  },
  {
    path: paths.organizations.details.root,
    taskPaths: paths.organizations.details.work.tasks,
  },
  {
    path: `${paths.projects.details.root}/*`,
    taskPaths: paths.projects.details.tasks,
  },
  {
    path: paths.projects.details.root,
    taskPaths: paths.projects.details.tasks,
  },
  {
    path: `${paths.vendors.details.root}/*`,
    taskPaths: paths.vendors.details.tasks,
  },
  {
    path: paths.vendors.details.root,
    taskPaths: paths.vendors.details.tasks,
  },
] as const;

export const resolveTaskPaths = (pathname: string): TaskRoutePaths | null => {
  const directMatch =
    TASK_CONTEXTS.find(taskPaths =>
      matchPath({ path: taskPaths.any, end: false }, pathname)
    ) ??
    TASK_CONTEXTS.find(taskPaths =>
      matchPath({ path: taskPaths.root, end: true }, pathname)
    );

  if (directMatch) return directMatch;

  return (
    TASK_PATH_FALLBACKS.find(({ path }) =>
      matchPath({ path, end: false }, pathname)
    )?.taskPaths ?? null
  );
};

export const matchesTaskPath = (pathname: string, path?: string, end = false) =>
  Boolean(path && matchPath({ path, end }, pathname));

export const isTaskCreateRoute = (pathname: string) => {
  const taskPaths = resolveTaskPaths(pathname);

  return matchesTaskPath(pathname, taskPaths?.new);
};

export const isTaskEditRoute = (pathname: string) => {
  const taskPaths = resolveTaskPaths(pathname);

  return matchesTaskPath(pathname, taskPaths?.edit);
};

export const getTaskDetailsTabRouteState = (
  pathname: string,
  taskPaths: TaskRoutePaths | null = resolveTaskPaths(pathname)
): TaskDetailsTabRouteState => {
  if (!taskPaths) return EMPTY_TASK_DETAILS_TAB_ROUTE_STATE;

  const isAddingSubtask = matchesTaskPath(pathname, taskPaths.subtasksNew);
  const editingSubtaskId =
    matchPath({ path: taskPaths.subtasksEdit, end: true }, pathname)?.params
      .subtaskId ?? null;
  const isAddingComment = matchesTaskPath(pathname, taskPaths.commentsNew);

  return {
    isSubtasksActive:
      isAddingSubtask ||
      Boolean(editingSubtaskId) ||
      matchesTaskPath(pathname, taskPaths.subtasks, true),
    isAddingSubtask,
    editingSubtaskId,
    isCommentsActive:
      isAddingComment || matchesTaskPath(pathname, taskPaths.comments, true),
    isAddingComment,
    isActivityLogActive: matchesTaskPath(pathname, taskPaths.activityLog),
  };
};

interface BuildTaskPathProps {
  path?: string;
  params: TaskRouteParams;
  taskId?: string;
  subtaskId?: string;
}

export const buildTaskPath = ({
  path,
  params,
  taskId,
  subtaskId,
}: BuildTaskPathProps) => {
  if (!path) return '';

  return generatePath(path, {
    ...params,
    ...(taskId && { taskId }),
    ...(subtaskId && { subtaskId }),
  });
};
